import { type Mesh, MeshBuilder } from "@babylonjs/core";
import { HeroId, Slot, fxk, hv } from "@rivalwatch/shared";
import type { Cue } from "../../audio/engine.ts";
import * as loops from "../../audio/cues/loops.ts";
import { bell, click, fm, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { ClientHero } from "../types.ts";

const V = { combo: 0, chi: 1, guard: 2, meditate: 3 } as const;
const FX = { Fist: fxk(HeroId.IronyFist, 0), Kick: fxk(HeroId.IronyFist, 1), Chi: fxk(HeroId.IronyFist, 2) } as const;
const D4 = 293.66;
const hz = (s: number) => D4 * 2 ** (s / 12);

const fistCue: Cue = (o) => {
  click(o, 0.22, 3800, 0.004);
  thump(o, 0.28, 190, 90, 0.08);
  fm(o, { f: 760, ratio: 2.6, index: 2.3, index1: 0, gain: 0.08, d: 0.11 });
  return 0.22;
};
const kickCue: Cue = (o) => {
  whoosh(o, 0.5, 350, 1800, 0.18);
  thump(o, 0.48, 140, 60, 0.13);
  return 0.38;
};
const chiCue: Cue = (o) => {
  tone(o, { f: hz(7), f1: hz(19), glide: 0.22, gain: 0.08, a: 0.05, d: 0.4 });
  noise(o, { color: "pink", type: "bandpass", f: 900, f1: 2600, gain: 0.25, a: 0.04, d: 0.42 });
  bell(o, hz(24), 0.25, 0.4);
  return 0.75;
};
const theme = {
  bpm: 148,
  octave: 1,
  voice: bell,
  notes: [
    [0, 0.25],
    [4, 0.25],
    [7, 0.5],
    [16, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

type IronyGear = { halo: Mesh; fists: Mesh[]; dragon: Mesh[] };

const hero: ClientHero<IronyGear> = {
  id: HeroId.IronyFist,
  bulk: 0.98,
  shape: { arms: 1.08, legs: 1.06 },
  gear(g) {
    const { scene, rig, add, addWorld, glow, toon } = g;
    const wraps = glow("#ffe680", 0.8);
    const gold = glow("#fff27a", 0.72);
    const deep = toon("#07341f");
    const fists: Mesh[] = [];
    for (const hand of [rig.handL, rig.handR]) {
      const fist = add(MeshBuilder.CreateSphere("chi-knuckle", { diameter: 0.22, segments: 10 }, scene), hand, wraps);
      fist.position.z = -0.08;
      fists.push(fist);
      const band = add(MeshBuilder.CreateTorus("hand-wrap", { diameter: 0.28, thickness: 0.03, tessellation: 16 }, scene), hand, toon("#272112"));
      band.rotation.x = Math.PI / 2;
    }
    const sash = add(MeshBuilder.CreateBox("irony-sash", { width: 0.12, height: 0.75, depth: 0.04 }, scene), rig.torso, toon("#2bb673"));
    sash.position.set(0.16, -0.14, -0.18);
    sash.rotation.z = -0.35;
    const dragon: Mesh[] = [];
    for (let i = 0; i < 7; i++) {
      const seg = add(MeshBuilder.CreateSphere("irony-dragon-scale", { diameter: 0.055 + i * 0.004, segments: 8 }, scene), rig.torso, gold);
      seg.position.set(-0.18 + Math.sin(i * 0.9) * 0.07, 0.28 - i * 0.075, -0.245);
      dragon.push(seg);
    }
    const head = add(MeshBuilder.CreateBox("irony-dragon-head", { width: 0.16, height: 0.1, depth: 0.05 }, scene), rig.torso, gold);
    head.position.set(-0.2, 0.34, -0.25);
    dragon.push(head);
    for (const side of [-1, 1]) {
      const whisker = add(MeshBuilder.CreateBox("dragon-whisker", { width: 0.16, height: 0.014, depth: 0.018 }, scene), rig.torso, deep);
      whisker.position.set(-0.2 + side * 0.09, 0.34, -0.28);
      whisker.rotation.z = side * 0.25;
      dragon.push(whisker);
    }
    const halo = addWorld(MeshBuilder.CreateTorus("chi-halo", { diameter: 1.5, thickness: 0.035, tessellation: 32 }, scene), glow("#f2c14e", 0.35));
    halo.setEnabled(false);
    return { halo, fists, dragon };
  },
  animate(gear, f) {
    const chi = (f.v[V.chi] ?? 0) > 0 || f.ultActive;
    const pulse = chi ? 1.25 + Math.sin(f.t * 18) * 0.16 : 1;
    for (const fist of gear.fists) fist.scaling.setAll(pulse);
    for (const [i, seg] of gear.dragon.entries()) seg.rotation.z = Math.sin(f.t * 5 + i) * 0.08;
    gear.halo.setEnabled(chi || (f.v[V.guard] ?? 0) > 0 || (f.v[V.meditate] ?? 0) > 0);
    gear.halo.position.set(f.pos.x, f.pos.y + 1.05, f.pos.z);
    gear.halo.rotation.y = f.t * (chi ? 8 : 2);
    gear.halo.scaling.setAll(chi ? 1.2 + Math.sin(f.t * 12) * 0.08 : 0.8);
    if ((f.v[V.meditate] ?? 0) > 0) return "sit";
    if (f.moveKind >= 16) return "attack-kick-right";
    if (f.action === 3 || f.action === 1) return "attack-melee-right";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: "Chi", value: Math.min(1, hv(c, eid, V.chi) / (5 * 60)), color: "#f2c14e" },
      { label: "Guard", value: Math.min(1, hv(c, eid, V.guard) / 60), color: "#ffe680" },
      { label: "Stillness", value: Math.min(1, hv(c, eid, V.meditate) / (3 * 60)), color: "#7dffb0" },
    ];
  },
  fx: {
    [FX.Fist]: { keepGeneric: true },
    [FX.Kick]: { keepGeneric: true },
    [FX.Chi]: { keepGeneric: true },
  },
  icons: {
    [Slot.Primary]: ["M7 14l5-8 5 8", "M6 18h12", "M9 10h6"],
    [Slot.Secondary]: ["M12 3l7 4v5c0 4-3 7-7 9-4-2-7-5-7-9V7z", "M9 12l2 2 4-5"],
    [Slot.Ability1]: ["M4 17l8-10 8 10", "M9 16h6", "M14 7l3-2"],
    [Slot.Ability2]: ["M12 5v14", "M5 12h14", "M7 7c3-3 7-3 10 0"],
    [Slot.Ultimate]: ["M5 19c4-9 10-14 14-14", "M9 19c2-5 5-8 10-9", "M6 7l3 1-1 3"],
    passive: ["M5 18c2-8 5-12 11-14", "M15 4l4 4-4 4", "M5 20h14"],
  },
  audio: {
    weight: "light",
    fire: () => ({ key: "fire:irony-fist:punch", cue: trim(8, fistCue) }),
    ability: { [Slot.Secondary]: trim(11, chiCue), [Slot.Ability1]: trim(5, kickCue), [Slot.Ability2]: trim(11, chiCue), [Slot.Ultimate]: trim(11, chiCue) },
    theme,
    themeTrim: { heroic: 2, hostile: -5.8, tease: 5.7 },
    loops: ({ v, ultActive, alive }) => (alive && (ultActive || v[V.chi] > 0) ? [{ key: "irony-chi", build: loops.levitate, gain: 0.35 }] : []),
  },
};

export default hero;
